import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useBranding } from '@/context/BrandingContext';
import { Loader2, X, Mail } from 'lucide-react';
import api from '@/utils/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(() => {
    const reason = localStorage.getItem('logout_reason');
    if (reason === 'idle') {
      localStorage.removeItem('logout_reason');
      return 'You were logged out due to inactivity. Please sign in again.';
    }
    return '';
  });
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const { login } = useAuth();
  const { branding, getLogoUrl } = useBranding();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      // Generic error message — do not reveal whether email exists or account is disabled
      setError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const response = await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotMessage(response.data.message);
    } catch (err) {
      setForgotMessage('If an account with that email exists, a password reset link has been sent.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/28e64fab-4b49-47e2-81b4-57b8629d4478/images/3cde3c3a277178e20e252f01e0d0d2facfe6adc974ec77660fa7b1d67c81faa6.png')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/80 to-secondary/60" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h1 className="text-5xl font-bold mb-4">{branding.company_name || 'Goldpeak Financial Solutions Pvt Ltd'}</h1>
          <p className="text-xl text-gray-200">{branding.tagline || 'We are with you'}</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src={getLogoUrl()}
              alt={branding.company_name}
              className="w-16 h-16 mx-auto mb-4 object-contain"
            />
            <h2 className="text-2xl font-bold text-foreground mb-1">Goldpeak Financial Solutions Pvt Ltd</h2>
            <p className="text-sm text-muted-foreground">Welcome to Goldpeak Loan DSA CRM</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {info && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="idle-info">
                {info}
              </div>
            )}
            {error && (
              <div
                data-testid="login-error"
                className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm"
              >
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="animate-spin" size={20} />}
              {loading ? 'Signing In...' : 'Login'}
            </button>

            <div className="text-center">
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => {
                  setShowForgot(true);
                  setForgotEmail(email);
                  setForgotMessage('');
                }}
                className="text-sm text-primary hover:underline"
              >
                Forgot Password?
              </button>
            </div>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-10">
            © {new Date().getFullYear()} Goldpeak Financial Solutions Pvt Ltd. All rights reserved.
          </p>
        </div>
      </div>

      {showForgot && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Mail size={20} className="text-primary" />
                Reset Password
              </h3>
              <button
                onClick={() => setShowForgot(false)}
                className="p-1 hover:bg-muted rounded"
                data-testid="close-forgot-modal"
              >
                <X size={20} />
              </button>
            </div>
            {forgotMessage ? (
              <div>
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 text-sm text-foreground mb-4">
                  {forgotMessage}
                </div>
                <button
                  onClick={() => {
                    setShowForgot(false);
                    setForgotMessage('');
                    setForgotEmail('');
                  }}
                  className="w-full py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Enter your registered email address. If an account exists, you'll receive password reset instructions.
                </p>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    data-testid="forgot-email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none"
                    placeholder="your@email.com"
                  />
                </div>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  data-testid="forgot-submit"
                  className="w-full py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {forgotLoading && <Loader2 className="animate-spin" size={18} />}
                  Send Reset Instructions
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
